<template>
  <div>
    <app-spinner v-if="!loaded" />
    <template v-else>
      <app-cells>
        <app-input
          @input.native="onTransactionSearch"
          v-model="search"
          class="input--200"
          type="search"
          placeholder="Search by RRN"
          name="search"
        />
        <app-select
          @input="onChangeFilter"
          v-model="filter_source"
          :options="source_options"
          :reduce="item => item.code"
          :display-field="option => option.name"
          label="code"
          placeholder="Source"
          class="select--200"
        />
        <app-select
          @input="onChangeFilter"
          v-model="filter_bank"
          :options="bank_options"
          :reduce="item => item.id"
          :display-field="option => option.short_name"
          label="code"
          placeholder="Bank"
          class="select--200"
        />
        <app-select
          @input="onChangeFilter"
          v-model="filter_channel"
          :options="channel_options"
          :reduce="item => item.code"
          :display-field="option => option.name"
          label="code"
          placeholder="Channel"
          class="select--200"
        />
      </app-cells>
      <app-cells><h2 class="title">Waiting confirmation</h2></app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #rrn="{ row }">
          <span v-if="!row.rrn" class="status-tag" :style="{ 'backgroundColor': '#e85c20' }">Missing</span>
          <template v-else>{{ row.rrn }}</template>
        </template>
        <template #bank="{ row }">
          {{ row.bank.short_name }} ({{ row.channel.name }})
        </template>
        <template #slip="{ row }">
          <a
            v-if="row.machine_slip"
            :href="row.machine_slip.file"
            target="_blank"
            class="status-tag"
            style="background-color: #439ad1"
          >
            View
          </a>
          <template v-else>-</template>
        </template>
        <template #amount="{ row }">{{ formatString(row.amount) }} $</template>
        <template #commission="{ row }">{{ formatString(row.commission) }} $</template>
        <template #settlement_amount="{ row }">{{ formatString(row.settlement_amount) }} $</template>
        <template #source="{ row }">{{ row.source.name }} (#{{ row.source_instance_id }})</template>
        <template #created="{ row }">
          <div v-if="row.created_by" class="table-default__controls">
            <div class="table-default__photo">
              <img
                v-tooltip="{ content: row.created_by.full_name, placement: 'top-start', offset: 10 }" :src="getUserAvatar(row.created_by.crop.crop_url)"
                alt="Avatar"
              >
            </div>
            {{ dateTimeFormat(row.created_at) }}
          </div>
          <template v-else>-</template>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
      <app-cells><h2 class="title">Received</h2></app-cells>
      <v-client-table
        :data="table_received.items"
        :columns="table_received.columns"
        :options="table_received.options"
        class="table-default"
        :class="{ 'table-default--loading': table_received_preloader }"
      >
        <template #rrn="{ row }">
          <span v-if="!row.rrn" class="status-tag" :style="{ 'backgroundColor': '#e85c20' }">Missing</span>
          <template v-else>{{ row.rrn }}</template>
        </template>
        <template #bank="{ row }">
          {{ row.bank.short_name }} ({{ row.channel.name }})
        </template>
        <template #slip="{ row }">
          <a
            v-if="row.machine_slip"
            :href="row.machine_slip.file"
            target="_blank"
            class="status-tag"
            style="background-color: #439ad1"
          >
            View
          </a>
          <template v-else>-</template>
        </template>
        <template #amount="{ row }">{{ formatString(row.amount) }} $</template>
        <template #commission="{ row }">{{ formatString(row.commission) }} $</template>
        <template #settlement_amount="{ row }">{{ formatString(row.settlement_amount) }} $</template>
        <template #source="{ row }">{{ row.source.name }} (#{{ row.source_instance_id }})</template>
        <template #created="{ row }">
          <div v-if="row.created_by" class="table-default__controls">
            <div class="table-default__photo">
              <img
                v-tooltip="{ content: row.created_by.full_name, placement: 'top-start', offset: 10 }" :src="getUserAvatar(row.created_by.crop.crop_url)"
                alt="Avatar"
              >
            </div>
            {{ dateTimeFormat(row.created_at) }}
          </div>
          <template v-else>-</template>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPaginationReceived"
            v-if="pagination_received.pages > 1"
            v-model="pagination_received.page"
            :page-count="pagination_received.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination_received.count" :count="table_received.items.length" :page="pagination_received.page" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import { getCardTypes, getFinanceBanks, getFinancePosTransactionsList, getFinancePosTransactionsSource } from '@/http'
import { format, parseISO } from 'date-fns'
import debounce from 'lodash/debounce'

export default {
  name: 'FinancePosTransactionPending',
  data() {
    return {
      loaded: false,
      search: '',
      filter_channel: null,
      filter_bank: null,
      filter_source: null,
      channel_options: [],
      bank_options: [],
      source_options: [],
      table_preloader: false,
      table_received_preloader: false,
      table: {
        items: [],
        columns: [
          'rrn',
          'bank',
          'slip',
          'amount',
          'commission',
          'settlement_amount',
          'source',
          'created',
        ],
        options: {
          headings: {
            'rrn': 'RRN',
            'bank': 'Bank (Channel)',
            'slip': 'Slip',
            'amount': 'Amount',
            'commission': 'Commission',
            'settlement_amount': 'Settlement amount',
            'source': 'Source',
            'created': 'Created at',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_received: {
        items: [],
        columns: [
          'rrn',
          'bank',
          'slip',
          'amount',
          'commission',
          'settlement_amount',
          'source',
          'created',
        ],
        options: {
          headings: {
            'rrn': 'RRN',
            'bank': 'Bank (Channel)',
            'slip': 'Slip',
            'amount': 'Amount',
            'commission': 'Commission',
            'settlement_amount': 'Settlement amount',
            'source': 'Source',
            'created': 'Created at',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_received: {
        pages: 1,
        page: 1,
        count: 1,
      },
    }
  },
  created() {
    Promise.all([
      this.fetchItems(),
      this.fetchItemsReceived()
    ])
      .then(() => {
        this.loaded = true
      })
    getCardTypes().then(response => { this.channel_options = response.data })
    getFinanceBanks().then(response => { this.bank_options = response.data })
    getFinancePosTransactionsSource().then(response => { this.source_options = response.data })
  },
  methods: {
    fetchTables() {
      this.fetchItems()
      this.fetchItemsReceived()
    },
    async fetchItems() {
      this.table_preloader = true
      const { data } = await getFinancePosTransactionsList({
        page: this.pagination.page,
        page_size: 20,
        received: false,
        search: this.search,
        source: this.search ? '' : this.filter_source,
        bank: this.search ? '' : this.filter_bank,
        channel: this.search ? '' : this.filter_channel,
      })
      this.table.items = data.results
      this.pagination.pages = data.pages
      this.pagination.count = data.count
      this.table_preloader = false
    },
    async fetchItemsReceived() {
      this.table_received_preloader = true
      const { data } = await getFinancePosTransactionsList({
        page: this.pagination_received.page,
        page_size: 20,
        received: true,
        search: this.search,
        source: this.search ? '' : this.filter_source,
        bank: this.search ? '' : this.filter_bank,
        channel: this.search ? '' : this.filter_channel,
      })
      this.table_received.items = data.results
      this.pagination_received.pages = data.pages
      this.pagination_received.count = data.count
      this.table_received_preloader = false
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onClickPaginationReceived(page) {
      this.pagination_received.page = page
      this.fetchItems()
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    formatString(string) {
      return string ? new Intl.NumberFormat('ru-RU').format(string) : '-'
    },
    onChangeFilter() {
      this.pagination.page = 1
      this.pagination_received.page = 1
      this.search = ''
      this.fetchTables()
    },
    onTransactionSearch() {
      this.searchTransaction(this)
    },
    searchTransaction: debounce(vm => {
      vm.pagination.page = 1
      vm.pagination_received.page = 1
      vm.fetchTables()
    }, 350),
  }
}
</script>
